import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { encryptId } from '../../utils/crypto';

function ProjectCard({ project, matchPercentage }) {
  const formattedDeadline = new Date(project.deadline).toLocaleDateString();
  const matchColor = matchPercentage >= 70 ? '#34d399' : matchPercentage >= 50 ? '#fbbf24' : '#f87171';
  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (matchPercentage / 100) * circumference;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-glass)', borderRadius: '16px',
        overflow: 'hidden', position: 'relative', transition: 'border-color 0.3s, transform 0.3s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = matchColor; e.currentTarget.style.transform = 'translateY(-4px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-glass)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Top accent */}
      <div style={{ height: '3px', background: `linear-gradient(90deg, ${matchColor}, var(--accent-violet))` }} />

      <div style={{ padding: '20px' }}>
        {project.isUrgent && (
          <span style={{
            position: 'absolute', top: '8px', right: '8px', background: 'rgba(248,113,113,0.15)', color: '#f87171',
            fontSize: '0.65rem', fontWeight: 700, padding: '3px 10px', borderRadius: '9999px', border: '1px solid rgba(248,113,113,0.3)',
          }}>Urgent</span>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px', fontFamily: 'var(--font-display)' }}>{project.title}</h2>
            <span style={{ fontSize: '0.75rem', color: 'var(--accent-violet)', background: 'rgba(124,58,237,0.1)', padding: '2px 10px', borderRadius: '9999px', border: '1px solid rgba(124,58,237,0.2)' }}>{project.type}</span>
          </div>
          {/* Match circle */}
          <div style={{ position: 'relative', width: '56px', height: '56px', flexShrink: 0, marginLeft: '12px' }}>
            <svg width="56" height="56" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="40" cy="40" r="36" fill="none" stroke="var(--border-subtle)" strokeWidth="5" />
              <circle cx="40" cy="40" r="36" fill="none" stroke={matchColor} strokeWidth="5"
                strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: matchColor }}>{matchPercentage}%</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gap: '6px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Budget</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{project.budget_range}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Deadline</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formattedDeadline}</span>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Required Tech</span>
          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {(project.skills ? project.skills.split(',') : []).map((s, i) => (
              <span key={i} style={{
                fontSize: '0.65rem', padding: '2px 8px', borderRadius: '9999px',
                background: 'rgba(0,212,255,0.08)', color: 'var(--accent-cyan)', border: '1px solid rgba(0,212,255,0.15)',
              }}>{s.trim()}</span>
            ))}
            {!project.skills && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>No skills specified</span>}
          </div>
        </div>

        <Link
          to={`/ProjectDetails/${encodeURIComponent(encryptId(project.client_project_id))}`}
          style={{
            display: 'block', textAlign: 'center', padding: '10px', borderRadius: '10px', textDecoration: 'none',
            background: 'linear-gradient(135deg, #10b981, #7c3aed)', color: '#fff', fontWeight: 600, fontSize: '0.85rem',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.target.style.opacity = '0.85'}
          onMouseLeave={e => e.target.style.opacity = '1'}
        >
          View Details
        </Link>
      </div>
    </motion.div>
  );
}

export default ProjectCard;